import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, MagnifyingGlass, UploadSimple, FilePdf, ImageSquare, X, CircleNotch,
  CheckCircle, CaretDown, Info,
} from "@phosphor-icons/react";
import { PageContainer } from "@/components/customer/PageContainer";
import { api, getErrorMessage } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";

const inputCls =
  "w-full bg-black/40 border border-neutral-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#73bf44] transition-colors placeholder:text-neutral-600";
const selectCls =
  "w-full appearance-none bg-black/40 border border-neutral-700 rounded-lg pl-3 pr-10 py-2.5 text-sm text-white outline-none focus:border-[#73bf44] transition-colors cursor-pointer";

const FALLBACK = {
  models: ["Adventure MK1", "Adventure Mk2", "Grenade", "Storm", "Infinity", "Galaxy", "GalaxyX", "Crossfire", "Traxx", "Other"],
  dealers: ["CHQ Yatala", "CHQ Townsville", "CHQ Adelaide", "CHQ Packenham", "4KRV", "Off-Grid Caravans Tasmania", "RV Centre WA"],
};

function guessModel(model, options) {
  if (!model) return "";
  const m = model.toLowerCase();
  const has = (s) => m.includes(s);
  let guess = "";
  if (has("grenade")) guess = "Grenade";
  else if (has("storm")) guess = "Storm";
  else if (has("infinity")) guess = "Infinity";
  else if (has("galaxyx") || has("galaxy x")) guess = "GalaxyX";
  else if (has("galaxy")) guess = "Galaxy";
  else if (has("crossfire")) guess = "Crossfire";
  else if (has("traxx")) guess = "Traxx";
  else if (has("adventure")) guess = has("mk2") || has("gen 2") || has("generation 2") || has(" 2") ? "Adventure Mk2" : "Adventure MK1";
  return options.includes(guess) ? guess : "";
}

const Label = ({ children, required }) => (
  <label className="text-sm text-neutral-300 mb-1.5 block">
    {children} {required && <span className="text-[#73bf44]">*</span>}
  </label>
);

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <div className="relative">
      <select value={value} onChange={onChange} data-testid={testid} className={selectCls}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <CaretDown size={16} className="text-neutral-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}

export default function Warranty() {
  const navigate = useNavigate();
  const { caravan } = useCustomer();
  const [options, setOptions] = useState(FALLBACK);
  const [form, setForm] = useState({
    chassis_number: caravan?.chassis_number || "",
    model: "",
    customer_name: caravan?.customer_name || "",
    dealer: "",
    warranty_issues: "",
    technical_justification: "",
    total_repair_cost: "",
    parts_required: "",
    submitted_by_name: caravan?.customer_name || "",
    submitted_by_email: "",
    additional_comments: "",
  });
  const [lookingUp, setLookingUp] = useState(false);
  const [quote, setQuote] = useState(null); // {storage_path, filename, ...}
  const [quoteUploading, setQuoteUploading] = useState(false);
  const [photos, setPhotos] = useState([]); // [{ref, name, url}]
  const [photoBusy, setPhotoBusy] = useState(0); // count uploading
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // claim id
  const quoteRef = useRef(null);
  const photoRef = useRef(null);

  useEffect(() => {
    api.get("/warranty/options").then(({ data }) => setOptions(data)).catch(() => setOptions(FALLBACK));
  }, []);

  // seed model guess once options + caravan are known
  useEffect(() => {
    if (caravan?.model && !form.model) {
      const g = guessModel(caravan.model, options.models);
      if (g) setForm((f) => ({ ...f, model: g }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, caravan]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const lookup = async () => {
    const ch = form.chassis_number.trim();
    if (!ch) return;
    setLookingUp(true);
    try {
      const { data } = await api.get(`/caravans/${encodeURIComponent(ch)}`);
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || data.customer_name || "",
        submitted_by_name: f.submitted_by_name || data.customer_name || "",
        model: f.model || guessModel(data.model, options.models),
      }));
      toast.success(`Found your ${data.model || "caravan"}.`);
    } catch {
      toast.error("We couldn't find that chassis number — you can still continue and enter your details.");
    } finally {
      setLookingUp(false);
    }
  };

  const uploadOne = async (file, kind) => {
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    const { data } = await api.post("/warranty/upload", fd, { timeout: 120000 });
    return data;
  };

  const onQuote = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/pdf$/i.test(file.name) && file.type !== "application/pdf") {
      toast.error("The repair quote must be a PDF.");
      return;
    }
    setQuoteUploading(true);
    try {
      const ref = await uploadOne(file, "quote");
      setQuote(ref);
      toast.success("Quote uploaded.");
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't upload the quote."));
    } finally {
      setQuoteUploading(false);
    }
  };

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = 50 - photos.length;
    if (room <= 0) { toast.error("You can attach up to 50 photos."); return; }
    const batch = files.slice(0, room);
    if (files.length > room) toast.error(`Only the first ${room} photo(s) were added (50 max).`);
    setPhotoBusy((n) => n + batch.length);
    for (const file of batch) {
      try {
        const ref = await uploadOne(file, "photo");
        setPhotos((prev) => [...prev, { ref, name: file.name, url: URL.createObjectURL(file) }]);
      } catch (err) {
        toast.error(getErrorMessage(err, `Couldn't upload ${file.name}`));
      } finally {
        setPhotoBusy((n) => Math.max(0, n - 1));
      }
    }
  };

  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  const canSubmit = useMemo(
    () =>
      form.chassis_number.trim() &&
      form.customer_name.trim() &&
      form.dealer.trim() &&
      form.total_repair_cost.trim() &&
      form.submitted_by_email.trim() &&
      form.warranty_issues.trim() &&
      quote &&
      photos.length > 0 &&
      !submitting &&
      !quoteUploading &&
      photoBusy === 0,
    [form, quote, photos, submitting, quoteUploading, photoBusy]
  );

  const submit = async () => {
    if (!form.dealer.trim()) { toast.error("Please select your selling dealer."); return; }
    if (!form.total_repair_cost.trim()) { toast.error("Please enter the total repair cost from your quote."); return; }
    if (!quote) { toast.error("Please attach the repair quote (PDF)."); return; }
    if (photos.length === 0) { toast.error("Please attach at least one photo of the issue."); return; }
    setSubmitting(true);
    try {
      const files = [
        { ...quote },
        ...photos.map((p) => ({ ...p.ref })),
      ];
      const { data } = await api.post("/warranty/claim", { ...form, files });
      setDone(data.id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't submit your claim. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <PageContainer overline="Warranty" title="Claim submitted">
        <div className="max-w-xl mx-auto text-center py-6" data-testid="warranty-success">
          <div className="w-16 h-16 rounded-full bg-[#73bf44]/10 border border-[#73bf44]/30 flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={34} weight="fill" className="text-[#73bf44]" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-white">Thanks — your warranty claim is in</h2>
          <p className="text-neutral-400 mt-3">
            Our warranty team has received your claim and it's now in the queue for review. We'll be in touch on{" "}
            <span className="text-white">{form.submitted_by_email}</span>. Keep this reference handy:
          </p>
          <div className="mt-4 inline-block font-mono text-sm bg-black/40 border border-neutral-800 rounded-lg px-4 py-2 text-[#73bf44]">
            {done.slice(0, 8).toUpperCase()}
          </div>
          <div className="mt-8 flex gap-3 justify-center">
            <button onClick={() => navigate("/")} className="border border-neutral-700 text-white rounded-full px-5 py-2.5 text-sm hover:border-neutral-500 transition-colors">Back to home</button>
            <button onClick={() => { setDone(null); setPhotos([]); setQuote(null); }} data-testid="warranty-another" className="bg-[#73bf44] text-[#050505] rounded-full font-semibold px-5 py-2.5 text-sm hover:bg-[#5fa338] transition-colors">Submit another claim</button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      overline="Warranty"
      title="Submit a Warranty Claim"
      subtitle="Submit your warranty claim directly to On The Move — no need to go through your dealer. You'll need a repair quote from your nominated repairer and photos of the issue."
    >
      <div className="max-w-3xl space-y-6" data-testid="warranty-page">
        {/* Caravan */}
        <section className="bg-[#141414] border border-neutral-800 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Your caravan</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label required>OTM Chassis Number</Label>
              <div className="flex gap-2">
                <input value={form.chassis_number} onChange={set("chassis_number")} onBlur={lookup} placeholder="e.g. OTM01526" className={inputCls} data-testid="warranty-chassis" />
                <button onClick={lookup} disabled={lookingUp} data-testid="warranty-lookup" className="shrink-0 inline-flex items-center gap-1.5 border border-neutral-700 text-white rounded-lg px-3 text-sm hover:border-[#73bf44] transition-colors disabled:opacity-60">
                  {lookingUp ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label required>Model</Label>
              <Select value={form.model} onChange={set("model")} options={options.models} placeholder="Select model…" testid="warranty-model" />
            </div>
            <div>
              <Label required>Selling dealer</Label>
              <Select value={form.dealer} onChange={set("dealer")} options={options.dealers} placeholder="Select dealer…" testid="warranty-dealer" />
            </div>
            <div>
              <Label required>Caravan owner name</Label>
              <input value={form.customer_name} onChange={set("customer_name")} placeholder="Full name on the caravan" className={inputCls} data-testid="warranty-customer-name" />
            </div>
          </div>
        </section>

        {/* The issue */}
        <section className="bg-[#141414] border border-neutral-800 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">The issue/s</h3>
          <div className="space-y-4">
            <div>
              <Label required>Warranty issue / fault</Label>
              <textarea value={form.warranty_issues} onChange={set("warranty_issues")} rows={4} placeholder="Describe the fault — what's happening, when it started, and any error messages." className={inputCls} data-testid="warranty-issues" />
            </div>

            {/* Photos */}
            <div>
              <Label required>Photos of the issue (up to 50)</Label>
              <input ref={photoRef} type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} data-testid="warranty-photos-input" />
              <button onClick={() => photoRef.current?.click()} disabled={photos.length >= 50} data-testid="warranty-photos-pick" className="w-full border border-dashed border-neutral-700 rounded-lg py-5 flex flex-col items-center gap-2 text-neutral-400 hover:border-[#73bf44] transition-colors disabled:opacity-60">
                <ImageSquare size={22} />
                <span className="text-sm">{photos.length >= 50 ? "Maximum 50 photos added" : "Add photos"}{photoBusy > 0 ? ` · uploading ${photoBusy}…` : ""}</span>
              </button>
              {photos.length > 0 && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-3" data-testid="warranty-photo-grid">
                  {photos.map((p, i) => (
                    <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-neutral-800">
                      <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                      <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="warranty-photo-remove">
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-neutral-600 mt-2">{photos.length}/50 photos attached</p>
            </div>

            <div>
              <Label>Parts you believe OTM needs to supply (if any)</Label>
              <textarea value={form.parts_required} onChange={set("parts_required")} rows={2} placeholder="List any parts required to complete the repair." className={inputCls} data-testid="warranty-parts" />
            </div>
          </div>
        </section>

        {/* Repair & documents */}
        <section className="bg-[#141414] border border-neutral-800 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Repair quote</h3>
          <div className="space-y-5">
            <div>
              <Label required>Total repair cost (from quote)</Label>
              <input value={form.total_repair_cost} onChange={set("total_repair_cost")} placeholder="e.g. $1,250" className={`${inputCls} max-w-xs`} data-testid="warranty-cost" />
            </div>

            {/* Quote PDF */}
            <div>
              <Label required>Repair quote (PDF)</Label>
              <div className="flex items-start gap-2 mb-3 bg-[#73bf44]/5 border border-[#73bf44]/20 rounded-lg px-3 py-2.5" data-testid="warranty-quote-disclaimer">
                <Info size={15} className="text-[#73bf44] mt-0.5 shrink-0" />
                <p className="text-xs text-neutral-400 leading-relaxed">
                  Please obtain a repair quote from a local caravan repair technician for the works required. A quote is required prior to any works being undertaken — please do not commence repairs until your claim has been reviewed and approved by the On The Move warranty team. Failure to follow this process may result in On The Move Caravans not accepting the claim, and not processing reimbursement of works conducted without the correct approval/s.
                </p>
              </div>
              <input ref={quoteRef} type="file" accept="application/pdf" className="hidden" onChange={onQuote} data-testid="warranty-quote-input" />
              {quote ? (
                <div className="flex items-center gap-3 bg-black/40 border border-[#73bf44]/30 rounded-lg px-4 py-3" data-testid="warranty-quote-file">
                  <FilePdf size={22} className="text-[#73bf44]" weight="duotone" />
                  <span className="text-sm text-white truncate flex-1">{quote.filename}</span>
                  <button onClick={() => setQuote(null)} className="text-neutral-400 hover:text-red-400" data-testid="warranty-quote-remove"><X size={16} /></button>
                </div>
              ) : (
                <button onClick={() => quoteRef.current?.click()} disabled={quoteUploading} data-testid="warranty-quote-pick" className="w-full border border-dashed border-neutral-700 rounded-lg py-5 flex flex-col items-center gap-2 text-neutral-400 hover:border-[#73bf44] transition-colors disabled:opacity-60">
                  {quoteUploading ? <CircleNotch size={22} className="animate-spin" /> : <UploadSimple size={22} />}
                  <span className="text-sm">{quoteUploading ? "Uploading…" : "Upload the repair quote (PDF)"}</span>
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-[#141414] border border-neutral-800 rounded-2xl p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Your contact details</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label required>Submitted by (name)</Label>
              <input value={form.submitted_by_name} onChange={set("submitted_by_name")} placeholder="Your name" className={inputCls} data-testid="warranty-by-name" />
            </div>
            <div>
              <Label required>Contact email</Label>
              <input type="email" value={form.submitted_by_email} onChange={set("submitted_by_email")} placeholder="you@email.com" className={inputCls} data-testid="warranty-by-email" />
            </div>
            <div className="sm:col-span-2">
              <Label>Additional comments</Label>
              <textarea value={form.additional_comments} onChange={set("additional_comments")} rows={2} placeholder="Anything else we should know." className={inputCls} data-testid="warranty-comments" />
            </div>
          </div>
        </section>

        <div className="flex items-start gap-2 text-xs text-neutral-500">
          <Info size={15} className="text-neutral-600 mt-0.5 shrink-0" />
          <span>Your claim and files are sent securely to the On The Move warranty team for review. Fields marked * are required, including the repair quote PDF.</span>
        </div>

        <div className="flex justify-end pb-4">
          <button onClick={submit} disabled={!canSubmit} data-testid="warranty-submit" className="inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-semibold px-7 py-3 hover:bg-[#5fa338] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? <CircleNotch size={18} className="animate-spin" /> : <ShieldCheck size={18} weight="fill" />}
            {submitting ? "Submitting…" : "Submit warranty claim"}
          </button>
        </div>
      </div>
    </PageContainer>
  );
}
